import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import LandingPage from './components/LandingPage';
import GoogleSignIn from './components/GoogleSignIn';
import DocumentChatInterface from './components/DocumentChatInterface';
import LegalPage from './components/LegalPage';
import SubscriptionPage from './components/SubscriptionPage';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import axios from 'axios';
import './styles/apple-theme.css';
import Settings from './components/Settings';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { DocumentProvider } from './context/DocumentContext';
import BankStatement from './components/BankStatement';
import { SubscriptionProvider } from './context/SubscriptionContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Protected Route Component with Subscription Check
const ProtectedRoute = ({ children, requireSubscription = true }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const verifyAuthAndSubscription = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const userData = localStorage.getItem('user');

        if (!token || !userData) {
          setIsAuthenticated(false);
          setIsChecking(false);
          return;
        }

        // Verify token is still valid
        const authResponse = await axios.get(`${API_URL}/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!authResponse.data.valid) {
          throw new Error('Invalid token');
        }

        setIsAuthenticated(true);

        if (requireSubscription) {
          const subResponse = await axios.get(`${API_URL}/subscriptions/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setHasSubscription(
            subResponse.data?.has_subscription && 
            (subResponse.data?.status === 'active' || subResponse.data?.status === 'trialing')
          );
          setSubscriptionData(subResponse.data);
        }
      } catch (error) {
        console.error('Auth/Sub verification failed:', error);
        // Clear invalid auth data
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };

    verifyAuthAndSubscription();
  }, [requireSubscription]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  if (requireSubscription && !hasSubscription) {
    return <Navigate to="/subscription" replace />;
  }

  if (React.isValidElement(children)) {
    return React.cloneElement(children, { subscriptionData });
  }
  return children;
};

function App() {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  // Add a ref to track the loading state
  const isLoadingRef = useRef(false);
  const timeoutRef = useRef(null);

  const fetchUserInfo = useCallback(async () => {
    // If already loading or no token, don't proceed
    if (isLoadingRef.current) return;
    
    const token = localStorage.getItem('access_token');
    if (!token) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set loading flag
    isLoadingRef.current = true;

    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const updatedUser = {
        ...response.data,
        access_token: token
      };
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Error fetching user info:', error);
    } finally {
      // Reset loading flag after a delay to prevent rapid subsequent calls
      timeoutRef.current = setTimeout(() => {
        isLoadingRef.current = false;
      }, 1000); // 1 second cooldown
    }
  }, []); // Empty dependency array

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const AppContent = () => {
    const handleSignIn = async (userData) => {
      const userToStore = {
        ...userData,
        access_token: userData.access_token
      };
      localStorage.setItem('access_token', userData.access_token);
      localStorage.setItem('user', JSON.stringify(userToStore));
      setUser(userToStore);
    };

    const handleSignOut = () => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      setUser(null);
    };

    const handleDeleteAccount = async () => {
      try {
        await axios.delete(`${API_URL}/auth/delete-account`, {
          headers: {
            'Authorization': `Bearer ${user?.access_token}`
          }
        });
        handleSignOut();
      } catch (error) {
        console.error('Error deleting account:', error);
        // Handle error (show notification, etc.)
      }
    };

    return (
      <Routes>
        <Route 
          path="/" 
          element={
            user ? (
              <Navigate to="/chat" replace />
            ) : (
              <LandingPage onSignIn={handleSignIn} />
            )
          } 
        />
        <Route 
          path="/settings" 
          element={
            user ? (
              <Settings 
                user={user}
                onDeleteAccount={handleDeleteAccount}
                onSubscriptionChange={fetchUserInfo}
              />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        
        {/* Protected Routes without Subscription Requirement */}
        <Route
          path="/subscription"
          element={
            <ProtectedRoute requireSubscription={false}>
              <SubscriptionPage user={user} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/subscription/success"
          element={
            <ProtectedRoute requireSubscription={false}>
              <SubscriptionSuccess />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes with Subscription Requirement */}
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <DocumentChatInterface 
                user={user} 
                onSignOut={handleSignOut}
                onNewDocument={() => {}}
              />
            </ProtectedRoute>
          }
        />

        <Route path="/legal" element={<LegalPage />} />

        <Route path="/bank-statement" element={
          <SubscriptionProvider>
            <BankStatement />
          </SubscriptionProvider>
        } />

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  };

  // Set up axios interceptors
  useEffect(() => {
    axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
  }, []);

  return (
    <GoogleOAuthProvider clientId="359641684294-i26ck46cm8vamua4n730t19a3m5dj64f.apps.googleusercontent.com">
      <BrowserRouter>
        <AuthProvider>
          <DocumentProvider>
            <WebSocketProvider>
              <ThemeProvider>
                <AppContent />
              </ThemeProvider>
            </WebSocketProvider>
          </DocumentProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;